import React, { useState, useEffect } from "react";
import "./AvailabilityTable.css";
import { API } from "../../../API";
import AdminWrapper from "../AdminWrapper";
import SwitchMenuBar from "../../../Components/SwitchMenuBar/SwitchMenuBar";
import ReactTable from "react-table-6";
import FormDatePicker from "../../../Components/FormField/FormDatePicker";
import FormMultiSelect from "../../../Components/FormField/FormMultiSelect";
import { getClass } from "../../../Helper";

function AvailabilityTablePage() {
  const [allTeachers, setAllTeachers] = useState([]);

  useEffect(() => {
    getAllTeachers().catch((err) => alert(err));
  }, []);

  async function getAllTeachers() {
    const response = await API.allTeachers();
    setAllTeachers(response.data.filter((t) => !t.deleted_at));
  }

  const toDayOfWeek = (numRep) => {
    switch (numRep) {
      case 0:
        return "Sunday";
      case 1:
        return "Monday";
      case 2:
        return "Tuesday";
      case 3:
        return "Wednesday";
      case 4:
        return "Thursday";
      case 5:
        return "Friday";
      case 6:
        return "Saturday";
      default:
    }
  };

  const uniformDateStr = (dateStr) => {
    let arr = dateStr.split("-");
    return arr[0].length === 4 ? dateStr : arr.reverse().join("-");
  };

  const today = new Date().toISOString().slice(0, 10);

  const tableRows = allTeachers
    .filter((teacher) => teacher.first_name && teacher.last_name)
    .map((teacher) => ({
      fullName: teacher.first_name + " " + teacher.last_name,
      email: teacher.email || "N.A.",
      phone: teacher.contact_number || "N.A.",
      state: teacher.state || "N.A.",
      suburb: teacher.suburb || "N.A.",
      canDeliver: teacher.can_delivers
        ? Object.values(teacher.can_delivers).reduce((acc, elt) => {
            if (elt instanceof Array) acc.push(...elt.filter((item) => !!item));
            return acc;
          }, [])
        : teacher.can_deliver
        ? teacher.can_deliver.map((obj) => obj.can_deliver)
        : [],
      availabilities:
        teacher.rules &&
        teacher.rules[0] &&
        Object.values(teacher.rules[0])
          .filter(
            (availability) => !availability.time.includes("Not Available")
          )
          .map((availability) => ({
            date: uniformDateStr(availability.date),
            day: toDayOfWeek(
              new Date(uniformDateStr(availability.date)).getDay()
            ),
            time: availability.time.includes("All")
              ? ["All day"]
              : availability.time.map((timeSlot) =>
                  timeSlot === "Morning"
                    ? "AM"
                    : timeSlot === "Afternoon"
                    ? "PM"
                    : timeSlot === "Evening"
                    ? "Eve"
                    : ""
                ),
          }))
          .filter((availability) => availability.date >= today),
    }));

  const BasicCell = ({ content }) => (
    <div className="basic-cell-container">
      <div className="basic-cell-child-element">{content}</div>
    </div>
  );

  const CanDeliverCell = ({ types }) => (
    <div className="can-deliver-cell-container">
      {types.map((type) => (
        <div key={type} className="can-deliver-cell-child-element">
          {type}
        </div>
      ))}
    </div>
  );

  const AvailabilitiesCell = ({ availabilities }) => (
    <div className="availabilities-cell-container">
      {availabilities.map((availability) => (
        <Availability availability={availability} key={availability.date} />
      ))}
    </div>
  );

  const Availability = ({ availability }) => (
    <div className="availabilities-cell-item">
      <span className="availabilities-date-item">{availability.date} </span>
      <span className="availabilities-day-item">{availability.day} </span>
      {availability.time.map((timeSlot, idx) => (
        <span key={idx}>{timeSlot} </span>
      ))}
    </div>
  );

  return (
    <AdminWrapper>
      <SwitchMenuBar isViewAsTeacher />
      <div className="all-availability-table-container">
        <h1 className="text-3xl font-bold text-center">All Availabilities</h1>
        <ReactTable
          className="-striped -highlight"
          defaultPageSize={10}
          defaultFilterMethod={(filter, row) => {
            const id = filter.pivotId || filter.id;
            return row[id] !== undefined
              ? String(row[id])
                  .toLowerCase()
                  .includes(filter.value.toLowerCase())
              : true;
          }}
          data={tableRows}
          columns={[
            {
              Header: "Name",
              accessor: "fullName",
              Cell: (props) => <BasicCell content={props.value} />,
              filterable: true,
              width: 180,
            },
            {
              Header: "Email",
              accessor: "email",
              Cell: (props) => <BasicCell content={props.value} />,
              filterable: true,
              width: 180,
            },
            {
              Header: "Phone",
              accessor: "phone",
              Cell: (props) => <BasicCell content={props.value} />,
              filterable: true,
              width: 120,
            },
            {
              Header: "State",
              accessor: "state",
              Cell: (props) => <BasicCell content={props.value} />,
              filterable: true,
              width: 100,
            },
            {
              Header: "Suburb",
              accessor: "suburb",
              Cell: (props) => <BasicCell content={props.value} />,
              filterable: true,
              width: 120,
            },
            {
              Header: "Can Deliver",
              accessor: "canDeliver",
              Cell: (props) =>
                props.row.canDeliver && props.row.canDeliver.length > 0 ? (
                  <CanDeliverCell types={props.row.canDeliver} />
                ) : (
                  <BasicCell content="N.A." />
                ),
              filterable: true,
              Filter: (props) => {
                return (
                  <FormMultiSelect
                    name="canDeliver"
                    id="canDeliver"
                    onChange={props.onChange}
                    options={getClass().map((cls) => ({
                      value: cls.key,
                      label: cls.key,
                    }))}
                    isSearchable
                    isMulti
                    noMargin
                  />
                );
              },
              filterMethod: (filter, row) => {
                const id = filter.pivotId || filter.id;
                return filter.value && filter.value.length
                  ? filter.value.every((selected) =>
                      row[id].includes(selected.value)
                    )
                  : true;
              },
              width: 300,
            },
            {
              Header: "Availabilities",
              accessor: "availabilities",
              Cell: (props) =>
                props.row.availabilities &&
                props.row.availabilities.length > 0 ? (
                  <AvailabilitiesCell
                    availabilities={props.row.availabilities}
                  />
                ) : (
                  <BasicCell content="N.A." />
                ),
              filterable: true,
              Filter: (props) => {
                let value = props.filter && props.filter.value;
                console.log(value);
                return (
                  <FormDatePicker
                    placeholderText="Select date"
                    selected={value ? new Date(props.filter.value) : null}
                    onChange={props.onChange}
                    isClearable
                    dateFormat="yyyy-MM-dd"
                    className="datepicker-2"
                    maxWidth
                    noMargin
                  />
                );
              },
              filterMethod: (filter, row) => {
                const id = filter.pivotId || filter.id;
                const selectedDate = filter.value
                  ? new Date(
                      filter.value -
                        filter.value.getTimezoneOffset() * 1000 * 60
                    )
                      .toISOString()
                      .slice(0, 10)
                  : "";
                return selectedDate
                  ? row[id] &&
                      row[id]
                        .map((availability) => availability.date)
                        .includes(selectedDate)
                  : true;
              },
              width: 360,
            },
          ]}
        />
      </div>
    </AdminWrapper>
  );
}

export default AvailabilityTablePage;
